import { useEffect, useId, useRef } from 'react';
import { trapTabKey } from '../lib/focusTrap';
import { useScrollLock } from '../hooks/useScrollLock';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: 'default' | 'destructive';
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Shared confirmation dialog for destructive actions. Confirmation-only: it does
 * NOT own the mutation or its error state — the parent flips `open` to false and
 * runs the async work, surfacing any failure in that surface's inline banner.
 *
 * Initial focus lands on Cancel (WAI-ARIA APG guidance for destructive actions).
 * Cancel/Escape/backdrop restore focus to the trigger; after confirm the parent
 * owns focus (the trigger is often removed or the view navigates away).
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'destructive',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const titleId = useId();
  const bodyId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<Element | null>(null);

  useScrollLock(open);

  useEffect(() => {
    if (!open) return;
    // Capture the trigger so cancel/Escape/backdrop can restore focus to it.
    triggerRef.current = document.activeElement;
    cancelRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const handleCancel = () => {
    const el = triggerRef.current;
    if (el instanceof HTMLElement && el.isConnected) el.focus();
    onCancel();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      handleCancel();
      return;
    }
    trapTabKey(e, panelRef.current);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-sm p-0 sm:p-4"
      onClick={handleCancel}
    >
      <div
        ref={panelRef}
        className="glass-panel bg-[var(--surface)] w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 shadow-warm animate-fade-in-up safe-bottom"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        tabIndex={-1}
      >
        <div
          aria-hidden
          className="mx-auto mb-4 h-1.5 w-10 rounded-full bg-[var(--border)] sm:hidden"
        />
        <h2 id={titleId} className="text-xl font-semibold text-[var(--text-primary)]">
          {title}
        </h2>
        <p id={bodyId} className="mt-2 text-sm text-[var(--text-secondary)]">
          {body}
        </p>
        <div className="mt-5 flex gap-2 justify-end">
          <button ref={cancelRef} type="button" onClick={handleCancel} className="btn btn-ghost">
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={tone === 'destructive' ? 'btn btn-danger' : 'btn btn-primary'}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
